// src/components/ContractModal.js
import { useRef, useState } from 'react';
import { FiX, FiDownload } from 'react-icons/fi';

const SECTIONS = [
  {
    title: '1. Services Provided',
    body: `Developer agrees to provide the following services:\n\n• Initial Website Development: Custom website, Vercel hosting and infrastructure setup.\n• Compensation: 50% to be paid up front, and the remaining 50% to be paid upon completion before the site is deployed.\n• Monthly Hosting and Support: Hosting and infrastructure maintenance, security updates, and uptime monitoring (approximately 1 hour/month).\n• Direct Support: Email support with 48-hour response time. Includes up to 1 hour per month of direct support for bug fixes, minor updates, and questions.`,
  },
  {
    title: '2. Fees and Payment',
    body: `• Monthly Fee: $75 per month, due on the 1st of each month.\n• Payment Method: Cash, Venmo, Zelle, or Wire Transfer.\n• Additional Support: Any support time beyond the included 1 hour per month will be billed at $75 per hour. Developer will receive approval from Client before billing any additional hours.\n• Support Time Policy: The included 1 hour of monthly support does not roll over to subsequent months ("use it or lose it").\n• Late Payment: If payment is not received within 7 days of the due date, a late fee of $40 will be applied. If payment is not received within 14 days, Developer reserves the right to suspend services until payment is made in full; payment for the full 6-month term is still expected.`,
  },
  {
    title: '3. Term Length',
    body: `• Initial Term: This agreement requires a minimum commitment of 6 months from the date the website goes live, during which Client agrees to pay the monthly fee of $75.\n• Renewal: After the initial term, this agreement will automatically renew on a month-to-month basis under the same terms unless either party provides written notice of termination as outlined in Section 9.\n• Early Termination by Client: If Client terminates before the end of the initial term, Client agrees to pay 50% of the remaining monthly fees owed for the remainder of that term.`,
  },
  {
    title: '4. Project Timeline',
    body: `Initial website development timeline will be confirmed following the discovery call. This estimate is contingent on Client providing necessary information in a timely manner. If Client does not respond to requests within 2 days, the project may be delayed. Developer will notify Client of any such delays.`,
  },
  {
    title: '5. Client Responsibilities',
    body: `Client agrees to:\n\n• Provide all necessary content, images, and information for the website in a timely manner.\n• Pay monthly hosting fees on time.\n• Notify Developer of any issues or needed updates promptly.`,
  },
  {
    title: '6. Developer Responsibilities',
    body: `Developer agrees to:\n\n• Build and maintain the website according to industry best practices.\n• Provide transparent time tracking for billable hours beyond the included monthly support.\n• Communicate before billing any additional hours.\n• Maintain the website and perform necessary security updates.`,
  },
  {
    title: '7. Limitations and Disclaimers',
    body: `• Search Engine Rankings: Developer makes no guarantees regarding placement in Google or other search engine rankings. Rankings depend on external factors beyond the Developer's control.\n• Uptime: While reasonable efforts will be made to maintain uptime, Developer is not responsible for outages caused by Vercel, third-party services, or circumstances beyond Developer's control.`,
  },
  {
    title: '8. Intellectual Property',
    body: `Client agrees not to modify the website code themselves or hire another developer to make changes without Developer's knowledge and consent, as this may affect the integrity of the work.`,
  },
  {
    title: '9. Termination',
    body: `• During Initial Term: Client may terminate with 30 days written notice and payment of the early termination fee specified in Section 3.\n• After Initial Term: Either party may terminate with 30 days written notice; no early termination fees apply.\n• Developer's Right to Terminate: Developer may terminate immediately if Client fails to pay monthly fees for more than 30 days.\n• Upon Termination: Client must pay all outstanding fees. Developer will provide necessary access credentials and documentation not part of Developer's core accounts. Client is responsible for migrating the website to another host.`,
  },
  {
    title: '10. Governing Law',
    body: `This agreement shall be governed by the laws of the applicable state. If any provision is found invalid or unenforceable, it shall be modified to the minimum extent necessary to be valid, or otherwise severed; the remaining provisions shall remain in effect.`,
  },
  {
    title: '11. Entire Agreement',
    body: `This constitutes the entire agreement and supersedes prior understandings. Any modifications must be in writing and signed by both parties.`,
  },
];

function ContractModal({ onClose, onAccept }) {
  const scrollRef = useRef(null);
  const [hasRead, setHasRead] = useState(false);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      setHasRead(true);
    }
  }

  return (
    <div className="contract-overlay" onClick={onClose}>
      <div className="contract-dialog" onClick={e => e.stopPropagation()}>

        <div className="contract-dialog-header">
          <div>
            <h2 className="contract-dialog-title">CS Devs — Website Services Agreement</h2>
            <p className="contract-dialog-sub">Scroll to the bottom to confirm you have read this contract.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <a
              href="/WEBSITE HOSTING AND SUPPORT AGREEMENT.pdf"
              download
              className="contract-download"
              aria-label="Download contract PDF"
            >
              <FiDownload size={15} />
              Download PDF
            </a>
            <button className="contract-close" onClick={onClose} aria-label="Close"><FiX size={20} /></button>
          </div>
        </div>

        <div className="contract-body" ref={scrollRef} onScroll={handleScroll}>
          <p className="contract-intro">
            This Website Services Agreement ("Agreement") is entered into between CS Devs ("Company")
            and the prospective client ("Client") upon submission of a project enquiry. By submitting
            a request through the CS Devs website, the Client acknowledges having read and agreed to
            the terms set out below. This Agreement does not constitute a binding contract until the
            Company formally accepts the project in writing and the initial deposit has been received.
          </p>

          {SECTIONS.map(s => (
            <div key={s.title} className="contract-section">
              <h3 className="contract-section-title">{s.title}</h3>
              <p className="contract-section-body" style={{ whiteSpace: 'pre-line' }}>{s.body}</p>
            </div>
          ))}

          <p className="contract-end-note">
            By reading this Agreement in full and proceeding with a project enquiry, the Client
            confirms their understanding of and agreement to all terms stated herein. CS Devs
            reserves the right to decline any project enquiry at its sole discretion.
          </p>
        </div>

        <div className="contract-dialog-footer">
          {!hasRead
            ? <p className="contract-scroll-hint">↓ Continue scrolling to confirm you have read this contract</p>
            : <p className="contract-scroll-done">✓ You have read the full contract</p>
          }
          <button
            className="btn-cta"
            disabled={!hasRead}
            onClick={() => { onAccept(); onClose(); }}
          >
            I Have Read &amp; Understood This Contract
          </button>
        </div>

      </div>
    </div>
  );
}

export default ContractModal;
